import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// Support both Vite (import.meta.env.VITE_*) and Next.js (process.env.NEXT_PUBLIC_*)
const supabaseUrl =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) ||
  '';

const supabaseAnonKey =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_ANON_KEY) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY) ||
  '';

// Helper to check if we're in development/demo mode
export function isDemoMode() {
  return !supabaseUrl || supabaseUrl.includes('placeholder');
}

// Only warn in the browser, not during build
if (isDemoMode() && typeof window !== 'undefined') {
  console.warn(
    'Supabase credentials not found. Running in demo mode. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY in .env.local'
  );
}

// Create the Supabase client safely — handles SSR/prerender where createClient may fail
let _supabase: SupabaseClient<any, 'public', any> | null = null;

function getSupabase(): SupabaseClient<any, 'public', any> {
  if (_supabase) return _supabase;

  try {
    if (isDemoMode()) {
      _supabase = createClient('https://placeholder.supabase.co', 'placeholder-anon-key', {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
        },
      });
    } else {
      _supabase = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      });
    }
  } catch {
    // During SSR prerender, createClient may fail — return a proxy that won't crash
    _supabase = new Proxy({} as SupabaseClient<any, 'public', any>, {
      get: (_target, prop) => {
        if (prop === 'auth') {
          return new Proxy({}, {
            get: () => () => Promise.resolve({ data: { user: null, session: null }, error: null }),
          });
        }
        if (prop === 'from') {
          return () => ({
            select: () => ({ data: [], error: null }),
            insert: () => ({ data: null, error: null }),
            update: () => ({ data: null, error: null }),
            delete: () => ({ data: null, error: null }),
          });
        }
        return () => {};
      },
    });
  }

  return _supabase;
}

// Export as a getter so it's lazily initialized
export const supabase = new Proxy({} as SupabaseClient<any, 'public', any>, {
  get: (_target, prop) => {
    const client = getSupabase();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
});

// Helper function to get the current user
export async function getCurrentUser() {
  if (isDemoMode()) return null;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  } catch {
    return null;
  }
}

// Helper function to get the current session
export async function getCurrentSession() {
  if (isDemoMode()) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  } catch {
    return null;
  }
}
